import { useCallback } from 'react'
import useBdApp, { checkBdApp } from './useBdApp'

declare let window: Window & {
  ReactNativeWebView: {
    postMessage: (message: string) => void
  }
}

type NavigateEventBus = {
  type: 'navigation'
  data: {
    navigate: 'goBack' | string
  }
}

type LoginEventBus = {
  type: 'logged'
  data: {
    jwt: string
  }
}

export type WebviewConfig = {
  id: string
  icon: string
  title: string
  url: string
}

export type TabsCreateEvent = {
  type: 'tabs-create'
  data: WebviewConfig[]
}

export type DrawerCreateEvent = {
  type: 'drawer-create'
  data: WebviewConfig[]
}

export type GetUserTokenEvent = {
  type: 'get-user-token'
  data: {
    status: 'idle' | 'pending' | 'logged' | 'not-logged'
    jwt?: string
  }
}

export type EventSelectedEvent = {
  type: 'event-selected'
  data: {
    id: number
    name: string
  }
}

export type DownloadEvent = {
  type: 'download'
  data: {
    dataInBase64: string
    fileName: string
    mimeType: string
  }
}

export type DownloadFromUrlEvent = {
  type: 'download-from-url'
  data: {
    url: string
    fileName: string
    mimeType: string
  }
}

export type TotpCancelEvent = {
  type: 'cancel-totp'
  data: {
    reason?: string
  }
}

export type TotpFailedEvent = {
  type: 'failed-totp'
  data: {
    reason?: string
  }
}

export type TotpSuccessEvent = {
  type: 'success-totp'
  data: {
    token: string
  }
}

export type TotpOpenEvent = {
  type: 'open-totp'
  data?: {
    cookies?: Record<string, string> | string
  }
}

export type ConsoleEventBus = {
  type: 'console-event-bus'
  data?: {
    log?: any
  }
}

export type RefreshUserTokenEventBus = {
  type: 'run-refresh-token'
  data: {
    status: 'refresh'
  }
}

type BdAppEventBus =
  | NavigateEventBus
  | LoginEventBus
  | TabsCreateEvent
  | DrawerCreateEvent
  | GetUserTokenEvent
  | EventSelectedEvent
  | DownloadEvent
  | DownloadFromUrlEvent
  | TotpCancelEvent
  | TotpFailedEvent
  | TotpSuccessEvent
  | TotpOpenEvent
  | ConsoleEventBus
  | RefreshUserTokenEventBus

type UseBdAppEventBus = {
  on: <T extends BdAppEventBus>(
    event: T['type'],
    callback: (data: T['data']) => void
  ) => () => void
  publish: <T extends BdAppEventBus>(event: T['type'], data: T['data']) => void
}

export const publish = <T extends BdAppEventBus>(
  event: T['type'],
  data: T['data']
) => {
  const { isNative } = checkBdApp()

  if (!isNative) {
    return
  }
  const { ReactNativeWebView } = window
  ReactNativeWebView?.postMessage(
    JSON.stringify({
      type: event,
      data,
    })
  )
}

export default function useBdAppEventBus(): UseBdAppEventBus {
  const { isNative } = useBdApp()

  const on = useCallback(
    <T extends BdAppEventBus>(
      event: T['type'],
      callback: (data: T['data']) => void
    ) => {
      if (!isNative) return () => undefined

      const handleMessage = (e: MessageEvent) => {
        try {
          const messageData =
            typeof e.data === 'string' ? JSON.parse(e.data) : e.data

          if (messageData.type === event) {
            callback(messageData.data)
          }
        } catch (error) {
          console.error('Error parsing message data:', error)
        }
      }

      window.addEventListener('message', handleMessage)

      return () => {
        window.removeEventListener('message', handleMessage)
      }
    },
    [isNative]
  )

  return {
    on,
    publish,
  }
}
